<template>
  <div>
    <HeaderNavbar :title="rfObject?.firstname || 'Account'" :breadcrumbs="listBreadcrumbs"></HeaderNavbar>
    <div class="container-lead">
      <div class="card" v-if="rfObject">
        <div class="row g-0">
          <div class="col-12 col-md-3 border-end">
            <div class="card-body">
              <h4 class="subheader">{{ $t('account.settings') }}</h4>
              <div class="list-group list-group-transparent">
                <a
                  v-for="tab in availableTabs"
                  :key="tab.key"
                  href="#"
                  @click.prevent="currentTab = tab.key"
                  class="list-group-item list-group-item-action d-flex align-items-center"
                  :class="{ active: currentTab === tab.key }"
                  >{{ tab.label }}</a
                >
              </div>
              <h4 class="subheader mt-4">{{ $t('account.experience') }}</h4>
              <div class="list-group list-group-transparent">
                <a href="mailto:mario.doering@prospective.ch" class="list-group-item list-group-item-action">{{
                  $t('account.experience.give_feedback')
                }}</a>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-9 d-flex flex-column">
            <div class="card-body" v-if="currentTab === 'profile'">
              <h2 class="mb-4">{{ $t('account.profile') }}</h2>
              <h3 class="card-title">{{ $t('account.profile.personal_details') }}</h3>
              <div class="row g-3">
                <div class="col-md-4">
                  <FormInput
                    v-model="rfObject.firstname"
                    :label="$t('account.profile.firstname')"
                    :errors="rfFunctions.errors('firstname')"
                    :disabled="!canEdit"></FormInput>
                </div>
                <div class="col-md-4">
                  <FormInput
                    v-model="rfObject.lastname"
                    :label="$t('account.profile.lastname')"
                    :errors="rfFunctions.errors('lastname')"
                    :disabled="!canEdit"></FormInput>
                </div>
              </div>
              <h3 class="card-title mt-4">{{ $t('account.profile.email') }}</h3>
              <p class="card-subtitle">{{ $t('account.profile.email_help') }}</p>

              <div class="row g-3">
                <div class="col-md-6">
                  <FormInput
                    type="email"
                    v-model="rfObject.email"
                    :errors="rfFunctions.errors('email')"
                    :disabled="!canEdit"></FormInput>
                </div>
              </div>

              <div v-if="canEdit">
                <h3 class="card-title mt-4">{{ $t('account.password.password') }}</h3>
                <p class="card-subtitle">
                  {{ $t('account.password.explain') }}
                </p>
                <div>
                  <a href="#" class="btn" @click.prevent="currentTab = 'password'">
                    {{ $t('account.password.set_new') }}</a
                  >
                </div>
              </div>

              <h3 class="card-title mt-4">{{ $t('account.profile.admin.title') }}</h3>
              <p class="card-subtitle">
                {{ $t('account.profile.admin_help') }}
              </p>
              <div>
                <FormInput
                  type="switch"
                  v-model="rfObject.is_admin"
                  class="form-switch-lg text-secondary ps-3"
                  :text="$t('account.profile.admin.text')"
                  :disabled="!currentUser?.is_admin">
                </FormInput>
              </div>
            </div>
            <div class="card-body" v-if="currentTab === 'password'">
              <h2 class="mb-4">{{ $t('account.password') }}</h2>
              <h3 class="card-title mt-4">{{ $t('account.password.change') }}</h3>
              <p class="card-subtitle">
                {{ $t('account.password.change_help') }}
              </p>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <FormInput
                      type="password"
                      v-model="newPassword"
                      :label="$t('account.password.new_field')"
                      :errors="rfFunctions.errors('password')"
                      :disabled="!canEdit">
                    </FormInput>
                  </div>

                  <div class="mb-3">
                    <FormInput
                      type="password"
                      v-model="newPasswordRepeat"
                      :label="$t('account.password.repeat_field')"
                      :errors="repeatPaswordErrors"
                      :disabled="!canEdit">
                    </FormInput>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="canEdit" class="card-footer bg-transparent mt-auto">
              <div class="btn-list justify-content-end">
                <LoadingButton @press="saveAndNavigate" class="btn btn-primary" :disabled="!canSave"
                  >{{ $t('account.profile.save') }} <IconCheck :size="17" class="ms-2"></IconCheck>
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { accountFields } from '@/autopilot/functions/accounts/accountFields';
import { accountWorkflowGuide } from '@/autopilot/functions/accounts/accountWorkflow';
import { buildPjasUri } from '@/func/entityLinks';
import { FormInput, LoadingButton } from '@prospective/lithium';

import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import { useRemoteForm, remoteFormOptions } from '@/form/remote';
import type { CustomerAccount } from '@/types/types';
import { IconCheck } from '@tabler/icons-vue';
import translator from '@/locale/translator';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';

/* --------------------------------- STATE -------------------------------- */

const router = useRouter();
const route = useRoute();
const newPassword = ref('');
const newPasswordRepeat = ref('');
const currentTab = ref(route.query?.password ? 'password' : 'profile');
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const routeReady = useAutopilotRouteReady();
useAutopilotContext('account_workflow', () => accountWorkflowGuide);

const rfOptions = remoteFormOptions('/v1/account/accounts/{id}');
rfOptions.autopilotKey = 'current_account_form';
rfOptions.autopilotDescription =
  'The account form currently open. Password is write-only: set it via current_account_form_set_fields ' +
  '(required when is_new is true). It is never shown in context.';
rfOptions.autopilotFields = () => accountFields;
rfOptions.autopilotSaveSensitive = true;
rfOptions.autopilotTransformer = (o) => ({
  id: o?.id,
  firstname: o?.firstname,
  lastname: o?.lastname,
  email: o?.email,
  is_admin: o?.is_admin,
  is_active: o?.is_active,
  password_set: newPassword.value.length > 0,
  link: o?.id ? buildPjasUri('account', o.id) : null,
});
rfOptions.autopilotSetFields = (patch) => {
  if (patch.password !== undefined) {
    const value = String(patch.password ?? '');
    newPassword.value = value;
    newPasswordRepeat.value = value;
    if (value) currentTab.value = 'password';
    // keep password off rfObject — prepareDataForSave reads newPassword; never echo it in context
    delete patch.password;
  }
};

const didComeFromResetPassword = computed(() => {
  return route.query.did_reset === '1';
});

rfOptions.createObject = () => {
  return {
    firstname: '',
    lastname: '',
    email: '',
    is_admin: false,
    is_active: true,
  };
};
rfOptions.prepareDataForSave = (data) => {
  if (newPassword.value.length > 0) {
    data.password = newPassword.value;
  }

  return data;
};
rfOptions.afterFetch = () => {
  routeReady();
};

const { rfObject, rfFunctions } = useRemoteForm<CustomerAccount>(rfOptions);

const availableTabs = [
  { key: 'profile', label: translator.get('account.profile') },
  { key: 'password', label: translator.get('account.password') },
];

/* -------------------------------- METHODS -------------------------------- */

const saveAndNavigate = async (stopLoading?: () => void) => {
  try {
    await rfFunctions.save(stopLoading);
    if (didComeFromResetPassword.value) {
      router.push({ name: 'home' });
    }
  } catch (error) {
    console.error('Error while saving account', error);
  } finally {
    stopLoading?.();
  }
};
/* ------------------------------- COMPUTED ------------------------------ */
const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.settings'), path: { name: 'settings' } },
  { label: translator.get('settings.accounts.title'), path: { name: 'settings.accounts' } },
  {
    label: translator.get('account.settings'),
    path: { name: 'settings.accounts.detail', params: { id: route.params.id } },
  },
]);

const isCurrentUser = computed(() => {
  return currentUser.value?.id === Number(route.params.id);
});

const canEdit = computed(() => {
  return currentUser.value?.is_admin || isCurrentUser.value;
});

const canSave = computed(() => {
  if (newPassword.value.length > 0) {
    return newPassword.value === newPasswordRepeat.value;
  }

  return true;
});

const repeatPaswordErrors = computed(() => {
  // to not confuse the user unnecessarily, we only show the error
  // when both password have the same length
  if (newPassword.value.length > newPasswordRepeat.value.length) {
    return [];
  }

  if (newPassword.value !== newPasswordRepeat.value) {
    return [translator.get('account.password.passwords_need_to_match')];
  }

  return [];
});
</script>

<style lang="scss" scoped></style>
